/* Lumina Lighting — Multi 4.0.3 + Globo required-option validation. */

const PROVIDER_SELECTOR = 'product-variant-provider';
const NATIVE_REQUIRED_SELECTOR = '[data-lumina-manual-required]';
const CART_BUTTON_SELECTOR = '[data-element="to-cart-button"]';
const DYNAMIC_CHECKOUT_SELECTOR = 'product-dynamic-checkout';
const MESSAGE_CLASS = 'lumina-option-validation-message';
const initialized = new WeakSet();

function isVisible(element) {
  if (!element || element.hidden) return false;
  const style = getComputedStyle(element);
  if (style.display === 'none' || style.visibility === 'hidden') return false;
  return Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length);
}

function optionKey(group) {
  return group?.dataset.luminaOptionName || '';
}

function requiredNativeGroups(provider) {
  return Array.from(provider.querySelectorAll(NATIVE_REQUIRED_SELECTOR));
}

function clearProvisionalNativeSelection(group) {
  group.classList.add('lumina-awaiting-selection');
  group.querySelectorAll('input[type="radio"]').forEach((input) => {
    input.checked = false;
  });
  group.querySelectorAll('.is-selected').forEach((element) => element.classList.remove('is-selected'));
  const heading = group.querySelector('.\\#product-variant-selector-item-heading-value');
  if (heading && heading.textContent !== 'Please select') heading.textContent = 'Please select';
  const current = group.querySelector('[data-element="current"]');
  if (current && current.textContent !== 'Please select') current.textContent = 'Please select';
}

function globoRoots(provider) {
  return Array.from(provider.querySelectorAll('.gpo-app, [data-gpo-product-options], [class*="gpo-app"]'));
}

function closestGloboGroup(element, root) {
  return element.closest(
    '.gpo-form__group, .gpo-element, .gpo-field, .gpo-group, fieldset, [class*="gpo-form__group"]'
  ) || element.parentElement || root;
}

function requiredGloboGroups(provider) {
  const groups = new Set();
  globoRoots(provider).forEach((root) => {
    root.querySelectorAll(
      '.gpo-required, [data-required="true"], [aria-required="true"], input[required], select[required], textarea[required], [class~="required"]'
    ).forEach((marker) => groups.add(closestGloboGroup(marker, root)));

    root.querySelectorAll('label, .gpo-label').forEach((label) => {
      if (/\*/.test(label.textContent || '')) groups.add(closestGloboGroup(label, root));
    });
  });

  return Array.from(groups).filter(isVisible);
}

function fieldHasValue(field) {
  if (field.disabled) return true;
  if (field.matches('input[type="radio"], input[type="checkbox"]')) return field.checked;
  if (field.matches('select')) return field.selectedIndex >= 0 && String(field.value || '').trim() !== '';
  return String(field.value || '').trim() !== '';
}

function globoGroupIsComplete(group) {
  const allFields = Array.from(group.querySelectorAll('input, select, textarea'))
    .filter((field) => !field.disabled && !field.matches('input[type="button"], input[type="submit"]'));
  const propertyFields = allFields.filter((field) =>
    field.name?.startsWith('properties[') && !field.name.startsWith('properties[_')
  );
  const fields = propertyFields.length ? propertyFields : allFields;
  if (!fields.length) return false;

  const radios = fields.filter((field) => field.type === 'radio');
  if (radios.length) return radios.some((field) => field.checked);
  const checkboxes = fields.filter((field) => field.type === 'checkbox');
  if (checkboxes.length) return checkboxes.some((field) => field.checked);

  const customerFields = fields.filter((field) => field.type !== 'hidden');
  if (customerFields.length) return customerFields.every(fieldHasValue);
  return fields.some(fieldHasValue);
}

function globoIsReady(provider) {
  const groups = requiredGloboGroups(provider);
  return groups.every(globoGroupIsComplete);
}

function hasGloboOptions(provider) {
  return globoRoots(provider).length > 0 || provider.querySelector('[name^="properties["]') !== null;
}

function ensureMessage(provider) {
  let message = provider.querySelector(`.${MESSAGE_CLASS}`);
  if (message) return message;
  const form = provider.querySelector('product-form');
  if (!form) return null;
  message = document.createElement('p');
  message.className = MESSAGE_CLASS;
  message.setAttribute('role', 'alert');
  message.setAttribute('aria-live', 'polite');
  form.insertAdjacentElement('beforebegin', message);
  return message;
}

function setButtonLocked(button, locked) {
  if (locked) {
    if (button.hasAttribute('data-lumina-purchase-locked')) return;
    if (!button.hasAttribute('data-lumina-purchase-locked')) {
      button.dataset.luminaPreviouslyDisabled = button.disabled ? 'true' : 'false';
    }
    button.disabled = true;
    button.setAttribute('aria-disabled', 'true');
    button.setAttribute('data-lumina-purchase-locked', '');
    return;
  }

  if (!button.hasAttribute('data-lumina-purchase-locked')) return;
  if (button.dataset.luminaPreviouslyDisabled !== 'true') button.disabled = false;
  button.removeAttribute('aria-disabled');
  button.removeAttribute('data-lumina-purchase-locked');
  delete button.dataset.luminaPreviouslyDisabled;
}

function validate(provider, manuallySelected, announce = false) {
  const nativeGroups = requiredNativeGroups(provider);
  nativeGroups.forEach((group) => {
    if (manuallySelected.has(optionKey(group))) {
      group.classList.remove('lumina-awaiting-selection');
    } else {
      clearProvisionalNativeSelection(group);
    }
  });

  const nativeComplete = nativeGroups.every((group) => manuallySelected.has(optionKey(group)));
  const globoComplete = globoIsReady(provider);
  const locked = !nativeComplete || !globoComplete;

  provider.querySelectorAll(CART_BUTTON_SELECTOR).forEach((button) => setButtonLocked(button, locked));
  provider.querySelectorAll(DYNAMIC_CHECKOUT_SELECTOR).forEach((checkout) => {
    if (checkout.classList.contains('lumina-purchase-locked') !== locked) {
      checkout.classList.toggle('lumina-purchase-locked', locked);
    }
    const ariaHidden = locked ? 'true' : 'false';
    if (checkout.getAttribute('aria-hidden') !== ariaHidden) checkout.setAttribute('aria-hidden', ariaHidden);
  });

  const message = ensureMessage(provider);
  if (message) {
    let text = '';
    if (!nativeComplete && !globoComplete) text = 'Please select the cable gauge and complete all required customization options above.';
    else if (!nativeComplete) text = 'Please select the cable gauge that matches your existing system.';
    else if (!globoComplete) text = 'Please complete all required Customize & Add to Cart options above.';
    if (message.textContent !== text) message.textContent = text;
    const messageHidden = !locked || (!announce && !hasGloboOptions(provider) && nativeGroups.length === 0);
    if (message.hidden !== messageHidden) message.hidden = messageHidden;
  }
  provider.dataset.luminaPurchaseReady = locked ? 'false' : 'true';
  return !locked;
}

function initializeProvider(provider) {
  if (initialized.has(provider)) return;
  initialized.add(provider);
  const manuallySelected = new Set();
  let queued = false;

  const queueValidation = (announce = false) => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      validate(provider, manuallySelected, announce);
    });
  };

  provider.addEventListener('change', (event) => {
    const nativeGroup = event.target.closest(NATIVE_REQUIRED_SELECTOR);
    if (nativeGroup) manuallySelected.add(optionKey(nativeGroup));
    queueValidation(false);
  }, true);
  provider.addEventListener('input', () => queueValidation(false), true);
  provider.addEventListener('click', () => queueValidation(false), true);

  provider.addEventListener('submit', (event) => {
    if (validate(provider, manuallySelected, true)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  new MutationObserver(() => queueValidation(false)).observe(provider, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['checked', 'selected', 'value', 'class', 'style', 'hidden']
  });
  queueValidation(false);
}

function scan(root = document) {
  if (root.matches?.(PROVIDER_SELECTOR)) initializeProvider(root);
  root.querySelectorAll?.(PROVIDER_SELECTOR).forEach(initializeProvider);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => scan());
} else {
  scan();
}

new MutationObserver((mutations) => {
  mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
    if (node.nodeType === Node.ELEMENT_NODE) scan(node);
  }));
}).observe(document.documentElement, { childList: true, subtree: true });
